import { PublicClientApplication } from '@azure/msal-browser';
import { OAUTH_CLIENTS } from './url_helper';

// MSAL Configuration for logout
const msalConfig = {
  auth: {
    clientId: OAUTH_CLIENTS.MICROSOFT,
    authority: "https://login.microsoftonline.com/common",
    redirectUri: "http://localhost:3000/login",
    navigateToLoginRequestUrl: false,
  },
  cache: {
    cacheLocation: "sessionStorage",
    storeAuthStateInCookie: false,
  },
  system: {
    allowRedirectInIframe: true,
    iframeHashTimeout: 6000,
    loadFrameTimeout: 0,
    asyncPopups: true,
    popupHashTimeout: 6000,
    popupWindowAttributes: {
      scrollbars: true,
      resizable: true,
      width: 500,
      height: 600
    }
  }
};

// MSAL Instance for logout
const msalInstance = new PublicClientApplication(msalConfig);

/**
 * Handle OAuth logout for both Google and Microsoft
 * @param {string} provider - 'google' or 'microsoft'
 * @param {string} accessToken - The OAuth access token (optional, for Google)
 */
export const handleOAuthLogout = async (provider, accessToken = null) => {
  try {
    console.log(`=== HANDLING ${provider.toUpperCase()} LOGOUT ===`);
    
    if (provider === 'google') {
      // Get access token from localStorage if not provided
      if (!accessToken) {
        accessToken = getOAuthAccessToken('google');
      }
      await handleGoogleLogout(accessToken);
    } else if (provider === 'microsoft') {
      await handleMicrosoftLogout();
    }
    
    console.log(`${provider} logout completed successfully`);
  } catch (error) {
    console.error(`Error during ${provider} logout:`, error);
    // Don't throw error as we still want to proceed with local logout
  }
};

/**
 * Handle Google OAuth logout
 * @param {string} accessToken - Google access token
 */
const handleGoogleLogout = async (accessToken) => {
  try {
    console.log('=== STARTING GOOGLE LOGOUT ===');
    
    // Revoke Google access token if available
    if (accessToken) {
      console.log('Revoking Google access token:', accessToken.substring(0, 10) + '...');
      const revokeUrl = `https://oauth2.googleapis.com/revoke?token=${accessToken}`;
      const response = await fetch(revokeUrl, { method: 'POST' });
      
      if (response.ok) {
        console.log('Google access token revoked successfully');
      } else {
        console.warn('Failed to revoke Google access token:', response.status);
      }
    } else {
      console.log('No Google access token found to revoke');
    }
    
    // Clear Google OAuth state from localStorage (only for this tab)
    localStorage.removeItem('googleUserDetails');
    console.log('Google user details cleared from localStorage');
    
    // Don't use iframe logout as it affects all tabs
    // Instead, just clear the local session data
    console.log('Google logout completed (token revoked, local data cleared)');
    
  } catch (error) {
    console.error('Google logout error:', error);
    // Still clear local data even if token revocation fails
    localStorage.removeItem('googleUserDetails');
  }
};

/**
 * Handle Microsoft OAuth logout
 */
const handleMicrosoftLogout = async () => {
  try {
    console.log('=== STARTING MICROSOFT LOGOUT ===');
    
    // Use the silent logout function instead of MSAL logout to avoid redirects
    silentMicrosoftLogout();
    
    console.log('Microsoft logout completed silently');
    
  } catch (error) {
    console.error('Microsoft logout error:', error);
    
    // Fallback: use silent logout again
    try {
      console.log('Using fallback silent Microsoft logout');
      silentMicrosoftLogout();
      console.log('Fallback silent Microsoft logout completed');
    } catch (fallbackError) {
      console.error('Microsoft fallback logout error:', fallbackError);
    }
  }
};

/**
 * Detect which OAuth provider was used for login
 * @returns {string|null} 'google', 'microsoft', or null
 */
export const detectOAuthProvider = () => {
  const googleUserDetails = localStorage.getItem('googleUserDetails');
  const microsoftUserDetails = localStorage.getItem('microsoftUserDetails');
  
  if (googleUserDetails) {
    return 'google';
  } else if (microsoftUserDetails) {
    return 'microsoft';
  }
  
  return null;
};

/**
 * Get OAuth access token if available
 * @param {string} provider - 'google' or 'microsoft'
 * @returns {string|null} Access token or null
 */
export const getOAuthAccessToken = (provider) => {
  try {
    if (provider === 'google') {
      const googleUserDetails = localStorage.getItem('googleUserDetails');
      if (googleUserDetails) {
        const details = JSON.parse(googleUserDetails);
        return details.access_token || null;
      }
    } else if (provider === 'microsoft') {
      const microsoftUserDetails = localStorage.getItem('microsoftUserDetails');
      if (microsoftUserDetails) {
        const details = JSON.parse(microsoftUserDetails);
        return details.access_token || null;
      }
    }
  } catch (error) {
    console.error('Error getting OAuth access token:', error);
  }
  
  return null;
};

/**
 * Comprehensive OAuth cleanup - clears all OAuth-related data
 */
export const clearAllOAuthData = () => {
  try {
    console.log('=== CLEARING ALL OAUTH DATA ===');
    
    // Clear all OAuth-related localStorage items
    localStorage.removeItem('googleUserDetails');
    localStorage.removeItem('microsoftUserDetails');
    console.log('OAuth localStorage items cleared');
    
    // Clear MSAL cache if available
    try {
      if (msalInstance) {
        msalInstance.clearCache();
        console.log('MSAL cache cleared');
      }
    } catch (msalError) {
      console.log('MSAL not initialized, skipping cache clear');
    }
    
    // Clear sessionStorage (used by MSAL)
    sessionStorage.clear();
    console.log('SessionStorage cleared');
    
    // Clear any OAuth-related cookies by domain
    const cookies = document.cookie.split(';');
    cookies.forEach(cookie => {
      const [name] = cookie.split('=');
      if (name.trim().includes('oauth') || 
          name.trim().includes('google') || 
          name.trim().includes('microsoft') ||
          name.trim().includes('msal')) {
        document.cookie = `${name.trim()}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
        console.log('Cleared cookie:', name.trim());
      }
    });
    
    // Additional Microsoft-specific cookie clearing
    const microsoftDomains = [
      'login.microsoftonline.com',
      'login.live.com',
      'account.microsoft.com'
    ];
    
    microsoftDomains.forEach(domain => {
      const msalCookieNames = [
        'msal.access_token.key',
        'msal.id_token.key',
        'msal.error',
        'msal.error.description',
        'msal.session.state',
        'msal.nonce.idtoken',
        'msal.client.info',
        'msal.state.login'
      ];
      
      msalCookieNames.forEach(cookieName => {
        document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; domain=${domain}; path=/;`;
        document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
      });
    });
    
    console.log('All OAuth data cleared successfully');
  } catch (error) {
    console.error('Error clearing OAuth data:', error);
  }
};

/**
 * Force Microsoft re-authentication by completely clearing MSAL state
 */
export const forceMicrosoftReAuth = () => {
  try {
    console.log('=== FORCING MICROSOFT RE-AUTHENTICATION ===');
    
    // Clear MSAL cache
    if (msalInstance) {
      msalInstance.clearCache();
      console.log('MSAL cache cleared for re-auth');
    }
    
    // Clear all MSAL-related sessionStorage
    const msalKeys = Object.keys(sessionStorage).filter(key => 
      key.includes('msal') || key.includes('microsoft')
    );
    msalKeys.forEach(key => {
      sessionStorage.removeItem(key);
      console.log('Removed MSAL key for re-auth:', key);
    });
    
    // Clear Microsoft user details
    localStorage.removeItem('microsoftUserDetails');
    
    // Force clear Microsoft cookies
    const microsoftDomains = [
      'login.microsoftonline.com',
      'login.live.com',
      'account.microsoft.com'
    ];
    
    microsoftDomains.forEach(domain => {
      const msalCookieNames = [
        'msal.access_token.key',
        'msal.id_token.key',
        'msal.error',
        'msal.error.description',
        'msal.session.state',
        'msal.nonce.idtoken',
        'msal.client.info',
        'msal.state.login'
      ];
      
      msalCookieNames.forEach(cookieName => {
        document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; domain=${domain}; path=/;`;
        document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
      });
    });
    
    console.log('Microsoft re-authentication forced');
  } catch (error) {
    console.error('Error forcing Microsoft re-auth:', error);
  }
};

/**
 * Silent Microsoft logout without any redirects or popups
 */
export const silentMicrosoftLogout = () => {
  try {
    console.log('=== SILENT MICROSOFT LOGOUT ===');
    
    // Clear MSAL cache
    if (msalInstance) {
      msalInstance.clearCache();
      console.log('MSAL cache cleared');
    }
    
    // Clear all sessionStorage
    sessionStorage.clear();
    console.log('SessionStorage cleared');
    
    // Clear Microsoft user details
    localStorage.removeItem('microsoftUserDetails');
    console.log('Microsoft user details cleared');
    
    // Clear all Microsoft-related cookies silently
    const microsoftDomains = [
      'login.microsoftonline.com',
      'login.live.com',
      'account.microsoft.com'
    ];
    
    const msalCookieNames = [
      'msal.access_token.key',
      'msal.id_token.key',
      'msal.error',
      'msal.error.description',
      'msal.session.state',
      'msal.nonce.idtoken',
      'msal.client.info',
      'msal.state.login',
      'msal.nonce.idtoken',
      'msal.client.info',
      'msal.state.login',
      'msal.session.state',
      'msal.error',
      'msal.error.description'
    ];
    
    microsoftDomains.forEach(domain => {
      msalCookieNames.forEach(cookieName => {
        // Clear with domain
        document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; domain=${domain}; path=/;`;
        // Clear without domain (current domain)
        document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
        // Clear with secure flag
        document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; secure;`;
        // Clear with httpOnly flag
        document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; httpOnly;`;
      });
    });
    
    // Use hidden iframes to silently clear Microsoft sessions
    const microsoftLogoutUrls = [
      'https://login.microsoftonline.com/common/oauth2/logout',
      'https://login.live.com/logout.srf',
      'https://account.microsoft.com/auth/complete-signout'
    ];
    
    microsoftLogoutUrls.forEach((url, index) => {
      setTimeout(() => {
        const iframe = document.createElement('iframe');
        iframe.style.display = 'none';
        iframe.style.width = '0';
        iframe.style.height = '0';
        iframe.style.border = 'none';
        iframe.src = url;
        document.body.appendChild(iframe);
        
        // Remove iframe after a short delay
        setTimeout(() => {
          if (document.body.contains(iframe)) {
            document.body.removeChild(iframe);
          }
        }, 1500);
      }, index * 300);
    });
    
    console.log('Silent Microsoft logout completed');
  } catch (error) {
    console.error('Error during silent Microsoft logout:', error);
  }
}; 